<template>
	<div
		class="shared-button-card"
		:class="{
			'shared-button-card--selected' : selected,
			'shared-button-card--horizontal': horizontal,
		}">
		<div class="shared-button-card__button">
			<span
				v-if="counter"
				class="shared-button-card__counter">
				{{ counter }}
			</span>

			<o-icon
				v-if="icon"
				v-tooltip="setup.displayedTitle"
				:font-icon="icon"/>

			<div
				v-else-if="company"
				class="shared-button-card__logo">
				<logo-company :company="company"/>
			</div>

			<div
				v-else
				class="shared-button-card__logo">
				<img src="@/assets/img/illustration/image.svg">
			</div>
		</div>

		<div class="shared-button-card__content">
			<p class="shared-button-card__title">{{ setup.displayedTitleShort }}</p>
			<p
				v-if="description"
				class="shared-button-card__description">
				{{ description }}
			</p>
		</div>

		<div
			v-if="bound"
			class="shared-button-card__bound"/>
	</div>
</template>

<script setup lang="ts">
// #region Setup
import type { SharedButtonCardEmits, SharedButtonCardProps } from './SharedButtonCardSetup';
import SharedButtonCardSetup from './SharedButtonCardSetup';
const emits = defineEmits<SharedButtonCardEmits>() as SharedButtonCardEmits;
const props = withDefaults(defineProps<SharedButtonCardProps>(), SharedButtonCardSetup.defaultProps);
const setup = new SharedButtonCardSetup(props, emits);
// #endregion
</script>

<style scoped lang="less">
/* stylelint-disable declaration-property-unit-allowed-list, declaration-property-value-no-unknown, function-no-unknown */
@import "@/styles/variables.less";
@import "@/styles/mixins.less";

.shared-button-card {
	cursor: pointer;
	position: relative;
	overflow: visible;
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 31%;
	max-width: 120px;
	margin-bottom: 20px;
	transition: opacity 0.2s;

	&__counter {
		position: absolute;
		top: -12px;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 40px;
		height: 23px;
		border-radius: 12px;
		font-size: 13px;
		font-weight: 600;
		background: white;
		box-shadow:
			0 2px 2px fade(black, 3),
			0 4px 4px fade(black, 3),
			0 8px 8px fade(black, 3),
			0 16px 16px fade(black, 3),
			0 32px 32px fade(black, 3);
	}

	&__content {
		.ellipsis();

		display: flex;
		flex-direction: column;
		gap: 3px;

		> p {
			.ellipsis();

			margin: 0;
		}
	}

	&__title {
		font-size: 14px;
		font-weight: 600;
		color: var(--grey-darker);
		text-overflow: ellipsis;
	}

	&__description {
		font-size: 14px;
		color: var(--grey-dark);
	}

	&__logo {
		overflow: hidden;
		border-radius: 15px;
	}

	&__button {
		display: flex;
		flex: 0 0 80px;
		align-items: center;
		justify-content: center;
		width: 80px;
		height: 80px;
		margin-bottom: 15px;
		border-radius: 25px;
		background-color: white;
		box-shadow:
			0 2px 2px fade(black, 3),
			0 4px 4px fade(black, 3),
			0 8px 8px fade(black, 3),
			0 16px 16px fade(black, 3),
			0 32px 32px fade(black, 3);
		transition: box-shadow 0.2s, transform 0.2s;

		.orion-icon {
			font-size: 30px;
			color: var(--grey);
		}
	}

	&__bound {
		height: 30px;
		margin-top: 10px;
		border-right: 3px dotted var(--grey-light);
		// transition: border-color .4s;
	}

	&--horizontal {
		position: relative;
		display: flex;
		flex-direction: row;
		gap: 20px;
		width: 100%;
		max-width: unset;

		.shared-button-card__button {
			margin-bottom: unset;
		}

		.shared-button-card__bound {
			position: absolute;
			right: -15px;
			width: 20px;
			height: 3px;
			height: 2px;
			border: unset;
			background: linear-gradient(to right, transparent 50%, var(--grey-light) 50%);
			background-size: 9px 2px, 100% 2px;
		}
	}

	&--selected {
		.shared-button-card__bound {
			transform-origin: top;
		}

		.shared-button-card__button {
			transform: translate3d(0, 7px, 0);
			box-shadow: unset;

			.orion-icon {
				color: var(--info);
			}
		}

		.shared-button-card__title,
		.shared-button-card__description {
			color: var(--info);
		}

		&.shared-button-card--horizontal {
			.shared-button-card__bound {
				background: linear-gradient(to right, transparent 50%, var(--info) 50%);
				background-size: 9px 2px, 100% 2px;
			}
		}
	}

	&:hover {
		.shared-button-card__button {
			transform: translate3d(0, 3px, 0);
			box-shadow: 0 1px 3px fade(black, 10);
		}

		.shared-button-card__title,
		.shared-button-card__description {
			opacity: 0.7;
		}
	}

	&:active {
		.shared-button-card__button {
			transform: translate3d(0, 5px, 0);
			box-shadow: unset;
			// box-shadow: inset 0px 1px 5px fade(black, 10);
		}
	}
}
</style>
